<template>
  <div
    class="self-stretch p-3 bg-fill-brand-secondary rounded-xl flex-col justify-end items-end gap-3 flex"
  >
    <form.Field name="name">
      <template #default="{ field }">
        <TextInput
          :model-value="field.state.value"
          :label="$t('channel.leaderboard.actionPointsEdit.actionName')"
          :placeholder="
            $t('channel.leaderboard.actionPointsEdit.enterActionName')
          "
          :readonly="!isNew"
          :error="field.state.meta.errors[0]?.message"
          @update:model-value="(val) => field.handleChange(val)"
        />
      </template>
    </form.Field>
    <form.Field name="title">
      <template #default="{ field }">
        <TextInput
          :model-value="field.state.value"
          :label="$t('channel.leaderboard.actionPointsEdit.actionTitle')"
          :placeholder="
            $t('channel.leaderboard.actionPointsEdit.enterActionTitle')
          "
          :error="field.state.meta.errors[0]?.message"
          @update:model-value="(val) => field.handleChange(val)"
        />
      </template>
    </form.Field>
    <form.Field name="shortTitle">
      <template #default="{ field }">
        <TextInput
          :model-value="field.state.value"
          :label="$t('channel.leaderboard.actionPointsEdit.actionShortTitle')"
          :placeholder="
            $t('channel.leaderboard.actionPointsEdit.enterActionShortTitle')
          "
          :error="field.state.meta.errors[0]?.message"
          @update:model-value="(val) => field.handleChange(val)"
        />
      </template>
    </form.Field>
    <form.Field name="notificationTitle">
      <template #default="{ field }">
        <TextInput
          :model-value="field.state.value"
          :label="
            $t('channel.leaderboard.actionPointsEdit.actionNotificationTitle')
          "
          :placeholder="
            $t(
              'channel.leaderboard.actionPointsEdit.enterActionNotificationTitle',
            )
          "
          :error="field.state.meta.errors[0]?.message"
          @update:model-value="(val) => field.handleChange(val)"
        />
      </template>
    </form.Field>
    <form.Field name="points">
      <template #default="{ field }">
        <TextInput
          :model-value="field.state.value"
          :label="$t('channel.leaderboard.actionPointsEdit.numberOfPoints')"
          type="number"
          :placeholder="
            $t('channel.leaderboard.actionPointsEdit.enterNumberOfPoints')
          "
          :error="field.state.meta.errors[0]?.message"
          @update:model-value="(val) => field.handleChange(val)"
        />
      </template>
    </form.Field>
    <button
      :disabled="item.is_logical || isDeleting"
      :title="
        item.is_logical
          ? $t('channel.leaderboard.actionPointsEdit.thisActionCannotBeDeleted')
          : ''
      "
      class="self-stretch h-10 px-6 py-1 bg-fill-brand-secondary rounded-lg justify-center items-center gap-1 inline-flex hover:bg-fill-brand-secondary-hover transition-colors text-center text-white text-sm font-medium leading-tight disabled:opacity-50 disabled:cursor-not-allowed"
      @click="confirmDeletion"
    >
      {{ $t("channel.leaderboard.actionPointsEdit.delete") }}
    </button>
    <button
      :disabled="isSaving"
      class="self-stretch h-10 px-6 py-1 bg-fill-brand-secondary rounded-lg justify-center items-center gap-1 inline-flex hover:bg-fill-brand-secondary-hover transition-colors text-center text-white text-sm font-medium leading-tight disabled:opacity-50 disabled:cursor-not-allowed"
      @click="form.handleSubmit()"
    >
      {{ $t("channel.leaderboard.actionPointsEdit.save") }}
    </button>
  </div>

  <SubtleConfirmationModal
    v-model="isConfirmingDelete"
    :title="$t('channel.leaderboard.actionPointsEdit.deleteAction')"
    :confirm-label="$t('channel.leaderboard.actionPointsEdit.delete')"
    @confirm="deleteAction"
  >
    <ConfirmationModalBody>
      {{ $t("channel.leaderboard.actionPointsEdit.deleteActionConfirmation") }}
    </ConfirmationModalBody>
  </SubtleConfirmationModal>
</template>

<script setup lang="ts">
import TextInput from "@/components/ui/form/text-input.vue";
import { useMutation } from "@vue/apollo-composable";
import { useForm } from "@tanstack/vue-form";
import { ref } from "vue";
import { z } from "zod/v4";
import { useToastStore } from "@/stores/toast.store";
import SubtleConfirmationModal from "@/components/ui/modal/subtle-confirmation-modal.vue";
import ConfirmationModalBody from "@/components/ui/modal/confirmation-modal/confirmation-modal-body.vue";
import { graphql as gql } from "@/generated";
import type { LocalAppAction } from "./action-points-edit.vue";
import { useAwaitAction } from "@/composables/use-await-action";
import { prettifyGraphqlError } from "@/utils/format";
import { useI18n } from "vue-i18n";

const CreateAppActionDocument = gql(/* GraphQL */ `
  mutation CreateAppAction($input: CreateAppActionInput!) {
    createAppAction(input: $input) {
      ... on AppActionForChannelAdmin {
        name
        points
      }

      ... on NotFoundError {
        error
      }
    }
  }
`);

const UpdateAppActionDocument = gql(/* GraphQL */ `
  mutation UpdateAppAction($input: UpdateAppActionInput!) {
    updateAppAction(input: $input) {
      ... on AppActionForChannelAdmin {
        name
        points
      }

      ... on NotFoundError {
        error
      }
    }
  }
`);

const DeleteAppActionDocument = gql(/* GraphQL */ `
  mutation DeleteAppAction($name: String!) {
    deleteAppAction(name: $name) {
      ... on AppActionSuccess {
        success
      }
      ... on NotFoundError {
        error
      }
    }
  }
`);

const { t } = useI18n();
const props = defineProps<{
  item: LocalAppAction;
  isNew: boolean;
}>();

const emit = defineEmits<{
  deleted: [];
  saved: [];
}>();

const toastStore = useToastStore();

const isConfirmingDelete = ref(false);

const schema = z.object({
  name: z
    .string()
    .min(1, t("channel.leaderboard.actionPointsEdit.actionNameRequired"))
    .regex(
      /^[A-Z0-9_]+$/,
      t(
        "channel.leaderboard.actionPointsEdit.actionNameCanOnlyContainUppercaseLettersNumbersAndUnderscores",
      ),
    ),
  title: z
    .string()
    .min(1, t("channel.leaderboard.actionPointsEdit.titleRequired")),
  shortTitle: z
    .string()
    .min(1, t("channel.leaderboard.actionPointsEdit.shortTitleRequired")),
  notificationTitle: z
    .string()
    .min(
      1,
      t("channel.leaderboard.actionPointsEdit.notificationTitleRequired"),
    ),
  points: z
    .number()
    .min(1, t("channel.leaderboard.actionPointsEdit.pointsMustBeAtLeast1"))
    .max(
      1000000,
      t("channel.leaderboard.actionPointsEdit.pointsCannotExceed1000000"),
    ),
});

const { mutate: createMutation } = useMutation(CreateAppActionDocument, {
  throws: "never",
  errorPolicy: "all",
});
const { mutate: updateMutation } = useMutation(UpdateAppActionDocument, {
  throws: "never",
  errorPolicy: "all",
});
const { mutate: deleteMutation } = useMutation(DeleteAppActionDocument, {
  throws: "never",
  errorPolicy: "all",
});

function confirmDeletion() {
  isConfirmingDelete.value = true;
}

const { handler: deleteAction, loading: isDeleting } = useAwaitAction(
  async () => {
    isConfirmingDelete.value = false;
    const response = await deleteMutation({ name: props.item.name });

    const data = response?.data?.deleteAppAction;
    if (!data || "error" in data) {
      toastStore.addToast({
        title: t("channel.leaderboard.actionPointsEdit.error"),
        message: `${t("channel.leaderboard.actionPointsEdit.failedToDeleteAction")}: ${
          data && "error" in data
            ? data.error
            : prettifyGraphqlError(response?.errors)
        }`,
        type: "error",
      });
    } else {
      toastStore.addToast({
        title: t("channel.leaderboard.actionPointsEdit.actionDeleted"),
        message: t(
          "channel.leaderboard.actionPointsEdit.actionDeletedSuccessfully",
        ),
        type: "success",
      });
      emit("deleted");
    }
  },
);

const { handler: saveAction, loading: isSaving } = useAwaitAction(
  async (values: {
    name: string;
    points: number;
    title: string;
    shortTitle: string;
    notificationTitle: string;
  }) => {
    const input = {
      name: values.name,
      points: values.points,
      title: values.title,
      short_title: values.shortTitle,
      notification_title: values.notificationTitle,
    };

    if (props.isNew) {
      const response = await createMutation({
        input,
      });

      const data = response?.data?.createAppAction;
      if (!data) {
        toastStore.addToast({
          title: t("channel.leaderboard.actionPointsEdit.error"),
          message: `${t("channel.leaderboard.actionPointsEdit.failedToCreateAction")}: ${prettifyGraphqlError(
            response?.errors,
          )}`,
          type: "error",
        });
      } else if ("error" in data) {
        toastStore.addToast({
          title: t("channel.leaderboard.actionPointsEdit.error"),
          message: data.error,
          type: "error",
        });
      } else {
        toastStore.addToast({
          title: t("channel.leaderboard.actionPointsEdit.actionCreated"),
          message: t(
            "channel.leaderboard.actionPointsEdit.actionCreatedSuccessfully",
          ),
          type: "success",
        });
      }
    } else {
      const response = await updateMutation({
        input,
      });

      const data = response?.data?.updateAppAction;
      if (!data) {
        toastStore.addToast({
          title: t("channel.leaderboard.actionPointsEdit.error"),
          message: `${t("channel.leaderboard.actionPointsEdit.failedToUpdateAction")}: ${prettifyGraphqlError(
            response?.errors,
          )}`,
          type: "error",
        });
      } else if ("error" in data) {
        toastStore.addToast({
          title: "Error",
          message: data.error,
          type: "error",
        });
      } else {
        toastStore.addToast({
          title: t("channel.leaderboard.actionPointsEdit.actionUpdated"),
          message: t(
            "channel.leaderboard.actionPointsEdit.actionUpdatedSuccessfully",
          ),
          type: "success",
        });
      }
    }

    emit("saved");
  },
);

const form = useForm({
  defaultValues: {
    name: props.item.name || "",
    title: props.item.title || "",
    shortTitle: props.item.short_title || "",
    points: props.item.points || 0,
    notificationTitle: props.item.notification_title || "",
  },
  validators: {
    onChange: schema,
  },
  onSubmit: async ({ value }) => {
    await saveAction(value);
  },
});
</script>
